export interface UserResponse {
  id: string
  hasSso: boolean
  attributes: UserInfo & UserAttributes
  // Legacy visual-signature option gates, still sent by the backend but no
  // longer stored or read — the user store strips this before patching
  permissions: unknown
}

const userRepository = (fetch: CustomFetch) => ({
  async register(registerData: RegisterUserPayload) {
    await fetch('/v1/register', {
      method: 'POST',
      body: {
        firstName: registerData.firstName,
        lastName: registerData.lastName,
        password: registerData.password,
        token: registerData.token,
        gtc: registerData.isGtcAccepted,
        lang: registerData.language,
        mobile: '', // FIXME: The backend requires a mobile number, but we actually never ask for it
        celloUCC: registerData.celloUCC || '',
      },
    })
  },
  async get(): Promise<UserResponse> {
    return fetch<UserResponse>('/v1/user?v2=true')
  },
  async update(updateData: Partial<UserInfo> & { currentPassword?: string }) {
    return fetch<{ status: string; message: string }>('/v1/user', {
      method: 'PUT',
      body: objectToSnakeCase(updateData),
    })
  },
  // FIXME: Should be DELETE with base path, but the backend uses POST
  async delete(password?: string) {
    await fetch('/v1/user/delete', {
      method: 'POST',
      body: { password },
    })
  },
  async getSignatureQualities(): Promise<SignatureQualities> {
    const signatureQualityData = await fetch<SignatureQualities>('/v3/user/signature-qualities-detail')
    return {
      ...signatureQualityData,
      // FIXME: We are still using snake_case for seals
      seal: objectToSnakeCase(signatureQualityData.seal),
    }
  },
  async updateAttributes(attributes: Partial<Record<keyof UserAttributes, unknown>>) {
    return fetch<{ status: string; message: string }>('/v1/user', {
      method: 'PUT',
      body: { attributes: objectToSnakeCase(attributes) },
    })
  },
  async logout() {
    await fetch('/v1/logout', { method: 'POST' })
  },
  async requestConfirmationEmail(email: string, language: string, flow: string = 'free', confirm = false) {
    await fetch('/v1/request-confirmation', {
      method: 'POST',
      body: {
        email,
        flow,
        lang: language,
        ...(confirm && { confirm }),
      },
    })
  },
  async verifyConfirmationToken(token: string, language: string) {
    return fetch<{ email: string; flow: string; status: string }>('/v1/valid-confirmation', {
      method: 'POST',
      body: { token, language },
    })
  },
  async validatePassword(password: string, token: string | null = null) {
    const { validPassword: isValid, error: errorCode } = await fetch<{
      validPassword: boolean
      error?: PasswordErrorCode
    }>('/v1/check-password', {
      method: 'POST',
      body: { password, ...(token && { token }) },
    })

    return { isValid, errorCode }
  },
  /**
   * Changes the password for the logged-in user.
   */
  async changePassword(newPassword: string, oldPassword: string) {
    return fetch<{ status: string; message: string }>('/v1/change-password', {
      method: 'POST',
      body: { new: newPassword, old: oldPassword },
    })
  },
  /**
   * Resets the password for a logged-out user.
   */
  async resetPassword(token: string, newPassword: string) {
    await fetch('/v1/reset-password', {
      method: 'POST',
      body: { token, new: newPassword },
    })
  },
  async requestPasswordReset(email: string, lang: string) {
    await fetch('/v1/request-reset', {
      method: 'POST',
      body: { email, lang },
    })
  },
  async acceptBusinessInvitation() {
    await fetch('/v1/user/accept', {
      method: 'POST',
      body: { business_invite: true },
    })
  },
  async acceptGeneralTerms() {
    await fetch('/v1/user/accept', {
      method: 'POST',
      body: { gtc: true },
    })
  },
  async acceptSwisscomTerms() {
    await fetch('/v1/user/accept', {
      method: 'POST',
      body: { tou_swisscom: true },
    })
  },
})

export default userRepository
